/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import * as getGamesAction from 'app/library/store/actions/get-library-games.action';
import { AssetsService } from 'app/shared/services/';
import { LibraryService } from '@library/services';
import { Constants } from '@shared/models/constants.model';
import * as fromStoreActions from 'app/library/store/actions';
import * as fromStoreReducers from 'app/library/store/reducers';

@Injectable()
export class GetLibraryGamesEffects {
  getGames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getGamesAction.GET_LIBRARY_GAMES),
      switchMap((action: getGamesAction.GetLibraryGames) => {
        this.libraryStore.dispatch(new fromStoreActions.GetBreakdownQueue(action.payload));
        const savedGameFilms$ =
          action.payload.userType === Constants.USER_TYPE.HEAD_COACH
            ? this.libraryService.getSavedGameFilms(action.payload.userId)
            : of([]);
        const purchaseIds = action.payload.userProducts.filter((up) => up.active).map((up) => up.id);
        const gameFilms$ = purchaseIds?.length ? this.libraryService.getGameFilms(purchaseIds) : of([]);

        return combineLatest([gameFilms$, savedGameFilms$]).pipe(
          map((games) =>
            this.mapLibraryGames([].concat(...games), action.payload.userType, action.payload.userProducts)
          ),
          catchError((error) => of(new getGamesAction.GetLibraryGamesFail(error.message)))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private libraryService: LibraryService,
    private assetsService: AssetsService,
    private libraryStore: Store<fromStoreReducers.LibraryState>
  ) {}

  private mapLibraryGames(assets, userType, products) {
    const libraryGames = assets.reduce((acc: any[], gameFilm: any) => {
      const matchingProduct = products.find((p) => p.event?.id === gameFilm.eventId);
      if (matchingProduct) {
        const newGame = {
          ...gameFilm,
          urlThumb: gameFilm.urlThumb || this.assetsService.getUrlThumb(gameFilm.url, gameFilm.is60fps),
          event: {
            ...matchingProduct.event,
            eventLink: this.assetsService.getEventLink(
              matchingProduct.event.name,
              matchingProduct.event.id,
              matchingProduct.event.startDate,
              matchingProduct.sport.name,
              userType === Constants.USER_TYPE.ADMIN
            ),
          },
        };
        return [...acc, newGame];
      } else {
        /* saved game film - college coaches */
        const newGame = {
          ...gameFilm,
          urlThumb: gameFilm.urlThumb || this.assetsService.getUrlThumb(gameFilm.url, gameFilm.is60fps),
          event: {
            id: gameFilm.eventId,
            name: gameFilm.eventName,
            eventLink: this.assetsService.getEventLink(
              gameFilm.eventName,
              gameFilm.eventId,
              gameFilm.eventStartDate,
              gameFilm.eventSportName,
              userType === Constants.USER_TYPE.ADMIN
            ),
          },
        };
        return [...acc, newGame];
      }
    }, []);
    return new getGamesAction.GetLibraryGamesSuccess(libraryGames);
  }
}
